// our utils
import { dataIsValid, dollarFormatter } from 'shared/utils';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Local Constants
const ADMIN_URL = process.env.REACT_APP_ADMIN_URL;

/**
 * @description This function is used to send an email to the client with the pricing information for a Securities Based Line.
 * @param {Object} params
 * @param {{advisorName: string, advisorEmail: string, advisorCompany: string}} params.advisorInfo
 * @param {{assetsAmount: string, annualIncome: string, address?: Object, clientId?: string, clientFullName: string, creditScoreValue: string, dateOfBirth: string, emailAddressValue: string, firstName: string, lastName: string}} params.clientInfo
 * @param {Object} params.financialData
 * @param {string} params.clientFullName - The full name of the client making the request
 * @param {string} params.loanAmount - The amount of the loan being requested
 * @param {string[]} [params.loanRelatedDetails] - Additional details related to the loan request
 * @param {string} [params.additionalNotes] - Any additional notes from the advisor making the request
 * @param {string} params.lendingTypeRequested - The type of lending being requested
 * @returns {string} - email template to be sent
 */
const lendingEmailTemplate = ({
	advisorInfo,
	clientInfo,
	financialData,
	clientFullName: fullNameValue,
	loanAmount,
	loanRelatedDetails,
	additionalNotes,
	lendingTypeRequested
}) => {
	const { advisorName, advisorEmail, advisorCompany } = advisorInfo;

	const {
		creditScoreValue: creditScore,
		clientId,
		emailAddressValue,
		annualIncome,
		assetsAmount
	} = clientInfo;

	const valuesToFormat = [
		{
			type: 'currency',
			value: annualIncome,
			label: "Client's Annual Income:"
		},
		{
			type: 'currency',
			value: assetsAmount,
			label: "Client's Assets Amount:"
		}
	];

	const formattedClientDetails = formatCollection(valuesToFormat)
		.split('\n')
		.map((item) => `<p style="margin: 2px 0;">${item}</p>`)
		.join('');

	const REQUIRED_FIELDS = [
		{ name: 'advisorName', value: advisorName },
		{ name: 'advisorEmail', value: advisorEmail },
		{ name: 'advisorCompany', value: advisorCompany },
		{ name: 'creditScore', value: creditScore },
		{ name: 'fullNameValue', value: fullNameValue },
		{ name: 'loanAmount', value: loanAmount },
		{ name: 'lendingTypeRequested', value: lendingTypeRequested }
	];

	REQUIRED_FIELDS.forEach(({ name, value }) => {
		if (!dataIsValid(value)) {
			throw new Error(`Missing required field ${name}`);
		}
	});

	const hasClientId = dataIsValid(clientId);
	const hasAddress = dataIsValid(clientInfo?.address);
	const hasEmailAddress = dataIsValid(emailAddressValue);

	const clientLookupUrl = hasClientId
		? `<p style="margin: 2px 0;">Client lookup url: <a href="${ADMIN_URL}/clients/${clientId}" target="_blank" rel="noopener noreferrer">Admin lookup link</a></p>`
		: '';

	const particularLoanDetails = loanRelatedDetails
		.map((item) => `<p style="margin: 2px 0;">${item}</p>`)
		.join('');

	let clientAddressDetails = '';

	let clientEmailDetails = '';

	if (hasAddress) {
		const { addressLine1, addressLine2, city, state, zipCode } =
			clientInfo.address;
		clientAddressDetails = `<p style="margin: 2px 0;">Client's address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}</p>`;
	}

	if (hasEmailAddress && !emailAddressValue.includes('contact+')) {
		clientEmailDetails = `<p style="margin: 2px 0;">Client's email: ${emailAddressValue}</p>`;
	}

	const message = `
		<div style="text-align: left;">
			<p style="margin: 2px 0;">The advisor ${advisorName} has priced a ${lendingTypeRequested} for one of their clients</p>
			
			<div style="border: 1px solid black; padding: 10px; margin-bottom: 10px;">
				<p style="margin: 2px 0;"> <strong>Advisor making request:</strong></p>
				<p style="margin: 2px 0;">Advisor's name: ${advisorName}</p>
				<p style="margin: 2px 0;">Advisor's email: ${advisorEmail}</p>
				<p style="margin: 2px 0;">Advisor's company: ${advisorCompany}</p>
			</div>

			<div style="border: 1px solid black; padding: 10px; margin-bottom: 10px;">
				<p style="margin: 2px 0;"> <strong>They made a request for the following client:</strong></p>
				${clientLookupUrl}
				<p style="margin: 2px 0;">Client's name: ${fullNameValue}</p>
				${clientEmailDetails}
				${formattedClientDetails}
				<p style="margin: 2px 0;">Client's Credit score: ${creditScore}</p>
				${clientAddressDetails}
			</div>

			<div style="border: 1px solid black; padding: 10px; margin-bottom: 10px;">
				<p style="margin: 2px 0;"> <strong>Loan related details:</strong></p>
				<p style="margin: 2px 0;">Requested loan amount: ${dollarFormatter.format(
					+loanAmount
				)}</p>
				${particularLoanDetails}
			</div>

			<div style="border: 1px solid black; padding: 10px;">
				<p style="margin: 2px 0;"> <strong>Additional Notes:</strong></p>
				${additionalNotes}
			</div>
		</div>
	`;

	return message;
};

export default lendingEmailTemplate;
